import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import Button from 'yoda-core-components/lib/components/Button';
import User from 'yoda-site-components/lib/helpers/User/User';
import SiteComponent from 'yoda-site-components/lib/components/SiteComponent/SiteComponent';
import {
    CANCEL_MESSAGE,
    CANCEL_ORDER_URL,
    TOTAL_REDUCED,
    CURBSIDE_OCP_CONTENT,
    ANALYTICS_PAGE_NAME_CHECKOUT_ORDER_CONFIRMATION,
    ANALYTICS_PAGE_NAME_PENDING_ORDER_CONFIRMATION,
} from 'yoda-checkout-components/lib/common/Constants';
import Formatter from 'yoda-core-components/lib/helpers/Formatter/Formatter';
import MessageBox from 'yoda-core-components/lib/components/MessageBox/MessageBox';
import { toCapitalize } from 'yoda-site-components/lib/helpers/Utils/utils';
import * as getCriteoDataAction from 'yoda-site-components/lib/actions/CriteoAction';
import clientLoggerAction from 'yoda-site-components/lib/actions/ClientLoggerAction';
import classNames from 'classnames/bind';
import IRISContent from 'yoda-checkout-components/lib/components/IRISContent/IRISContent';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import CheckoutAnalyticsAction from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import {
    isAgentLogged,
    isSentToAgencyFromCart,
} from 'yoda-core-components/lib/helpers/Utils/Utils';
import * as orderConfirmationActions from '../../../../../actions/OrderConfirmationAction';
import * as accountActions from '../../../../../actions/CreateAccountPostOrderAction';
import * as checkoutPageActions from '../../../../../actions/CheckoutPageActions';
import * as nativeOrderConfirmationActions from '../../../../../actions/NativeOrderConfirmationAction';
import * as styles from './ConfirmationMsg.css';
import OrderDetailsLink from '../OrderDetailsLink/OrderDetailsLink';
// eslint-disable-next-line import/no-named-as-default
import OrderNumber from '../OrderNumber/OrderNumber';
import VoucherNumber from '../ConfirmationVoucherNumber/ConfirmationVoucherNumber';
import InlineLoader from '../../../../../components/InlineLoader/InlineLoader';
import checkoutStaticText from '../../../../../helpers/LabelConstantsCheckout';
import PromiseDeliveryDate from '../../../../../components/PromiseDeliveryDates/PromiseDeliveryDates';

const cx = classNames.bind(styles);

class OrderConfirmationMessage extends SiteComponent {
    hydrateOnMount() {
        const {
            actions,
            sccOrder,
            context: { isNative = false, featureFlags: { enableOCPKibanaLog = true } = {} } = {},
            orderDetails,
            mountOnServer,
            orderId,
            enableCriteo,
            orderIdLocalStorage,
        } = this.props;

        actions.setMountOnServer(mountOnServer);
        const payload = {
            orderId,
        };
        if (sccOrder) {
            payload.scc = 'true';
        }
        isEmpty(orderDetails) && actions.getOrderDetails(payload);

        // if fetch has happened already on server
        if (!__SERVER__ && isNative && !isEmpty(orderDetails)) {
            actions.postOrderDetailsToNative();
        }

        // CRITEO action
        /* istanbul ignore if */
        if (!__SERVER__ && !isEmpty(orderDetails)) {
            if (isSentToAgencyFromCart(this.props.context)) {
                // B2b client on click of Sent to Agnecy
                this.props.actions.pageLoadSuccessAnalyticsAction(
                    ANALYTICS_PAGE_NAME_PENDING_ORDER_CONFIRMATION
                );
            } else {
                /* istanbul ignore next */
                if (enableOCPKibanaLog)
                    this.props.actions?.logError(
                        'OCPage_load_before_trigger',
                        `orderNumber: ${orderIdLocalStorage} orderId: ${orderId}`
                    );
                this.props.actions.pageLoadSuccessAnalyticsAction(
                    ANALYTICS_PAGE_NAME_CHECKOUT_ORDER_CONFIRMATION
                );
            }

            if (enableCriteo) {
                const criteoItems = orderDetails?.data?.bag?.items?.map((item) => {
                    return {
                        sku: item?.sku?.id,
                        quantity: item?.sku?.quantity,
                        price: item?.prices[1]?.amount,
                    };
                });
                if (!isEmpty(criteoItems) && criteoItems?.length > 0) {
                    this.props.actions.getCriteoData({
                        pageType: 'OrderConfirmation', // For OC page
                        transaction: orderDetails?.data?.number,
                        item: criteoItems.map((item) => item.sku).join('|'), // 'sku1|sku2'
                        price: criteoItems.map((item) => item.price).join('|'), // 'price1|price2'
                        quantity: criteoItems.map((item) => item.quantity).join('|'), // 'qty1|qty2'
                    });
                }
            }
        }
    }

    handleNativeCancelOrder = () => {
        /**
         Send order number to native, App will handle redirection to respective place
        * */
        const {
            orderInfo: { number },
            actions: { postCancelOrderDetailsToNative },
        } = this.props;
        postCancelOrderDetailsToNative(number);
    };

    getContentForCancelOrder = (orderIdNumber) => {
        const {
            orderInfo,
            context: { isNative },
        } = this.props;
        const cancelOrderLink =
            CANCEL_ORDER_URL + (orderInfo && orderInfo.number ? orderInfo.number : orderIdNumber);
        return isNative ? (
            <Button
                automationId="at-native-cancel-order-button"
                buttonType="Link"
                size="Sm"
                onClick={this.handleNativeCancelOrder}
            >
                {checkoutStaticText.CANCEL_ORDER}
            </Button>
        ) : (
            <a
                automationId="atCancelServiceLink"
                href={cancelOrderLink}
                rel="noopener noreferrer"
                className={`${styles.customerServiceLink} ${dt([
                    'inline-block',
                    'underline',
                    'text-black',
                ])}`}
            >
                {checkoutStaticText.CANCEL_ORDER}
            </a>
        );
    };

    render() {
        const {
            orderInfo,
            orderBillingInfo,
            orderContactInfo,
            orderTotals,
            createAccountPostOrderData,
            cancelOrderElapseTime,
            isNative,
            orderDetails,
            orderIdLocalStorage,
            orderUserNameLocalStorage,
            orderUserEmailLocalStorage,
        } = this.props;

        const orderIdNumber =
            orderInfo && orderInfo.number ? orderInfo.number : orderIdLocalStorage;
        let orderUserName;
        if (orderBillingInfo && orderBillingInfo.firstName) {
            orderUserName = orderBillingInfo.firstName;
        } else if (orderUserNameLocalStorage) {
            orderUserName = orderUserNameLocalStorage;
        } else {
            orderUserName = TokenProvider.get('DP_FIRST_NAME');
        }
        const orderUserEmail =
            orderContactInfo && orderContactInfo.emailId
                ? orderContactInfo.emailId
                : orderUserEmailLocalStorage;

        /* istanbul ignore if */
        if (
            !User.isUserLoggedIn() &&
            (!isEmpty(orderContactInfo.emailId) || !isEmpty(orderUserEmail)) &&
            !createAccountPostOrderData.checkAccountAPICalled
        ) {
            this.props.actions.getAccountByEmailId(orderUserEmail);
        }
        if (orderInfo.showInlineLoader) {
            return <InlineLoader context={this.props.context} />;
        }
        if (isEmpty(orderInfo)) {
            return null;
        }

        const B2BClientOrder = isSentToAgencyFromCart(this.props.context);
        const isAgent = isAgentLogged(this.props.context);
        const voucherNumber = TokenProvider.get('Voucher_Number') || '';
        return (
            <div
                id="orderConfirmationMsg"
                className={`${styles.orderConfirmationMsg} ${dt([
                    'font-open-sans-regular',
                    'leading-6',
                    'text-sm',
                ])}`}
            >
                {this.props.potd && (
                    <div className={cx('col12')}>
                        <MessageBox
                            type="success"
                            level="section"
                            hasClose={false}
                            title={`${TOTAL_REDUCED} $${orderTotals['ORDER AMOUNT']}`}
                        />
                    </div>
                )}
                <h3
                    data-automation-id="orderConfirmationMsg"
                    className={`${styles.orderConfirmationTitle} ${dt([
                        'md:text-2xlarge',
                        'sm:text-xlarge',
                        'font-open-sans-bold',
                    ])}`}
                    data-qm-block="true"
                    data-tlprivate
                >
                    {`Thank you for your order, ${toCapitalize(orderUserName)}!`}
                </h3>
                <div
                    data-automation-id="confirmationEmail"
                    className={`${styles.orderConfirmationInfoBlock} ${dt([
                        'inline-block',
                        'mt-2',
                        'text-medium',
                    ])}`}
                >
                    {B2BClientOrder ? (
                        <p>
                            This order is pending approval by your agency. Approval times may vary
                            but are typically 24–48 hours. You will receive a confirmation email
                            sent to &nbsp;
                            <strong
                                className={`${styles.confirmationEmail} ${dt(['inline'])}`}
                                data-qm-block="true"
                            >
                                {orderUserEmail}
                            </strong>
                            &nbsp; once your order is approved.
                            <br />
                            <br />
                            <br />
                            Please note that your shipping and contact information will be shared
                            with your agent. Contact your agent if you need to cancel this order.
                        </p>
                    ) : (
                        <>
                            <p className={dt(['inline'])}>
                                A confirmation email will be sent shortly to:&nbsp;
                            </p>
                            <strong
                                className={`${styles.confirmationEmail} ${dt(['inline'])}`}
                                data-qm-block="true"
                            >
                                {orderUserEmail}
                            </strong>
                        </>
                    )}
                    {orderContactInfo.phoneNumber && (
                        <p className={`${styles.confirmationText} ${dt(['block'])}`}>
                            Order status updates will be sent to:&nbsp;
                            <strong
                                className={dt(['smOnly:block'])}
                                data-qm-block="true"
                                data-tlprivate
                            >
                                {Formatter.format(
                                    orderContactInfo.phoneNumber,
                                    Formatter.TYPE.PHONE
                                )}
                            </strong>
                        </p>
                    )}
                </div>
                {orderInfo.showCurbsideBanner && (
                    <div
                        className={`${cx('curbsideBanner')} ${dt(['text-center', 'mt-4', '!p-0'])}`}
                        data-automation-id="at-curbside-banner"
                    >
                        <IRISContent componentName={CURBSIDE_OCP_CONTENT} />
                    </div>
                )}
                <div>
                    <PromiseDeliveryDate orderItems={orderDetails?.data?.bag?.items} isOcpPage />
                </div>
                <div className={`${cx('row', 'orderInfoWrap')} ${dt(['!m-0'])}`}>
                    <div
                        className={`${cx('orderDetailsWrapper')} ${dt([
                            '!py-6',
                            '!px-0',
                            'smMd:text-normal',
                            'flex',
                        ])}`}
                        data-automation-id="at-ocp-orderdetail-wrapper"
                    >
                        <div
                            className={`${cx('sm6', 'md6', 'lg6', 'ordrDtlBlock')} ${dt([
                                'md:align-middle',
                                '!p-0',
                                'smOnly:align-top',
                                'smOnly:w-full',
                            ])}`}
                        >
                            {/* To display order number */}
                            {B2BClientOrder && orderInfo?.id && (
                                <OrderNumber orderId={orderInfo.id} />
                            )}
                            {!B2BClientOrder && orderIdNumber && (
                                <OrderNumber orderId={orderIdNumber} />
                            )}
                        </div>
                        {voucherNumber && (isAgent || B2BClientOrder) && (
                            <div
                                className={`${cx('sm6', 'md6', 'lg6', 'voucherNumber')} ${dt([
                                    'm-0',
                                    '!pr-0',
                                    '!flex',
                                    'justify-end',
                                ])}`}
                            >
                                <VoucherNumber voucherNumber={voucherNumber} />
                            </div>
                        )}
                        <div
                            className={`${cx('sm6', 'md6', 'lg6', 'orderDetailsLink')} ${dt([
                                'm-0',
                                '!pr-0',
                                '!flex',
                                'justify-end',
                            ])}`}
                        >
                            {/* To update link on failover case */}
                            {!B2BClientOrder && (
                                <OrderDetailsLink
                                    isNative={isNative}
                                    OCPPagePass={orderInfo?.number || ''}
                                    cancelOrderLink={`${CANCEL_ORDER_URL}${orderIdNumber}`}
                                />
                            )}
                        </div>
                    </div>
                    <div>
                        <div
                            className={`${cx('horizontalDivider')} ${dt([
                                'h-[1px]',
                                'bg-gray-15',
                                'py-0',
                                'px-4',
                            ])}`}
                        />
                        {/* b2b voucher code  */}
                        {!B2BClientOrder && !isAgent && (
                            <div
                                className={`${cx('customerServiceText')} ${dt([
                                    '!leading-5',
                                    '!text-sm',
                                ])}`}
                                data-automation-id="at-customer-service-text-block"
                            >
                                <span>
                                    {CANCEL_MESSAGE}&nbsp;{cancelOrderElapseTime}.
                                </span>
                                &nbsp;&nbsp;
                                <span className={dt(['smMd:block', 'smMd:!pt-1'])}>
                                    {this.getContentForCancelOrder(orderIdNumber)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const {
        orderInfo,
        orderBillingInfo,
        orderContactInfo,
        orderTotals,
        createAccountPostOrderData,
        orderDetails,
        checkoutPage,
        context,
    } = state;
    const { enableCheckoutServerSideRendering = true, enableCriteo = false } =
        context?.featureFlags || {};
    const mountOnServer =
        (checkoutPage && checkoutPage.mountOnServer) ||
        (enableCheckoutServerSideRendering &&
            get(state, 'context.headers.cookies.disabledCheckoutSSR', false) !== 'true');
    const cancelOrderElapseTime = get(state, 'context.preferences.cancelOrderElapseTime', '');
    const isNative = get(state, 'context.isNative', false);
    return {
        orderInfo,
        orderBillingInfo,
        orderContactInfo,
        orderTotals,
        createAccountPostOrderData,
        orderDetails,
        mountOnServer,
        cancelOrderElapseTime,
        context,
        isNative,
        enableCriteo,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        merge(
            {},
            orderConfirmationActions,
            accountActions,
            checkoutPageActions,
            nativeOrderConfirmationActions,
            getCriteoDataAction,
            CheckoutAnalyticsAction,
            clientLoggerAction
        ),
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationMessage);
